import { Badge, Box, Button, Flex, Grid, GridItem, HStack, Stack, Text } from "@chakra-ui/react";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";
import type { AgentToolDescriptor, RiskTier } from "./agentTypes";
import { entityLabel, flattenLeaves, formatLeafValue, leafLabelKey } from "./confirmationCardFormatting";
import { momentLocaleFor } from "../formatting/i18nFormatters";

export type PendingConfirmation = {
  label: string;
  riskTier: RiskTier;
  descriptor: AgentToolDescriptor;
  args: Record<string, unknown>;
  // The planner's own accompanying text for the proposed call (see buildSystemPrompt) — a short
  // Norwegian summary of what will change, shown above the field breakdown.
  summary: string | null;
};

type LookupEntity = (id: string) => Record<string, unknown> | undefined;

// Human labels for the leaf-key identifiers `leafLabelKey` resolves to (the catalog carries raw
// OpenAPI property names, arbitrarily nested). Literal t(...) so langen extracts them; unmapped
// leaves fall back to the raw key.
const useLeafLabel = (): ((path: string[]) => string) => {
  const { t } = useTranslation();
  const labels: Record<string, string> = {
    Answer: t("Answer"),
    Title: t("Title"),
    Description: t("Description"),
    "Internal note": t("Internal note"),
    "Winning bids": t("Winning bids"),
    "Estimated value": t("Estimated value"),
    "Contract type": t("Contract type"),
    Counterparty: t("Counterparty"),
    Value: t("Value"),
    "No end date": t("No end date"),
    Responsible: t("Responsible"),
    "Start date": t("Start date"),
    "End date": t("End date"),
  };
  return (path) => {
    const key = leafLabelKey(path);
    return labels[key] ?? key;
  };
};

const bodyParamOf = (descriptor: AgentToolDescriptor) => descriptor.params.find((param) => param.in === "body");

type BodyField = { name: string; required: boolean };

const bodyFieldsOf = (schema: Record<string, unknown> | undefined): BodyField[] => {
  const properties = (schema?.properties as Record<string, unknown> | undefined) ?? {};
  const required = new Set((schema?.required as string[] | undefined) ?? []);
  return Object.keys(properties).map((name) => ({ name, required: required.has(name) }));
};

const ArrayValueView = ({ value, lookupEntity }: { value: unknown[]; lookupEntity: LookupEntity }) => (
  <Stack spacing="0.5">
    {value.map((item) => {
      const id = String(item);
      return (
        <Text key={id} fontSize="sm" fontWeight="semibold" color="smText">
          {entityLabel(lookupEntity(id)) || id}
        </Text>
      );
    })}
  </Stack>
);

// Read-only confirmation: the user describes the action in chat, the agent turns it into
// structured args, and this just *displays* them for a yes/no. To change something the user
// chats again (the agent re-proposes) — no inline form editing. Catalog-driven: path params
// resolve (via the entity index) to a "Concerns" line, body fields are shown as a summary,
// required-but-missing fields block confirm. No per-endpoint code.
export const ConfirmationCard = ({
  pending,
  lookupEntity,
  onConfirm,
  onCancel,
  isBusy,
}: {
  pending: PendingConfirmation;
  lookupEntity: LookupEntity;
  onConfirm: (args: Record<string, unknown>) => void;
  onCancel: () => void;
  isBusy: boolean;
}) => {
  const { t, i18n } = useTranslation();
  const leafLabel = useLeafLabel();
  const locale = momentLocaleFor(i18n.language);
  const resolveEntityName = (id: string) => entityLabel(lookupEntity(id)) || undefined;

  const { descriptor, riskTier, args } = pending;
  const binding = riskTier === "binding_statutory";
  const body = bodyParamOf(descriptor);
  const payload = body && typeof args[body.name] === "object" ? (args[body.name] as Record<string, unknown>) : {};

  const concerns = descriptor.params
    .filter((param) => param.in === "path")
    .map((param) => entityLabel(lookupEntity(typeof args[param.name] === "string" ? (args[param.name] as string) : "")))
    .filter((label) => label.length > 0);

  const fields = bodyFieldsOf(body?.schema);
  const fieldLeaves = (field: BodyField) => flattenLeaves([field.name], payload[field.name]);
  const present = fields.filter((field) => fieldLeaves(field).length > 0);
  const missing = fields.filter((field) => field.required && fieldLeaves(field).length === 0);

  return (
    <Box borderWidth="1px" borderColor="smBorder" borderRadius="xl" bg="smBackground" boxShadow="sm" p="4">
      <Flex align="center" justify="space-between" gap="3" mb="3">
        <Text fontSize="md" fontWeight="semibold" color="smText">
          {pending.label}
        </Text>
        <Badge
          bg={binding ? "smWarningSoft" : "smStatusNeutralBg"}
          color={binding ? "smWarning" : "smStatusNeutralText"}
          px="2"
          py="0.5"
          borderRadius="md"
          fontSize="xs"
          textTransform="none"
          flexShrink="0"
        >
          {binding ? t("Binding") : t("Reversible")}
        </Badge>
      </Flex>

      {pending.summary && (
        <Text fontSize="sm" color="smText" mb="3" lineHeight="tall" whiteSpace="pre-wrap">
          {pending.summary}
        </Text>
      )}

      {binding && (
        <Text fontSize="xs" color="smWarning" mb="3">
          {t("Binding action — it may notify suppliers and start statutory deadlines.")}
        </Text>
      )}

      {concerns.length > 0 && (
        <HStack spacing="2" fontSize="sm" mb="3" pb="3" borderBottomWidth="1px" borderColor="smBorder">
          <Text color="smMuted" fontWeight="medium" flexShrink="0">
            {t("Concerns")}
          </Text>
          <Text color="smText" fontWeight="semibold">
            {concerns.join(", ")}
          </Text>
        </HStack>
      )}

      {present.length > 0 && (
        <Box borderWidth="1px" borderColor="smBorder" borderRadius="lg" bg="smBackgroundSecondary" p="3">
          <Grid templateColumns="minmax(120px, auto) 1fr" columnGap="4" rowGap="3">
            {present.flatMap((field) =>
              fieldLeaves(field).map((entry) => (
                <Fragment key={entry.path.join(".")}>
                  <GridItem>
                    <Text fontSize="xs" color="smMuted" fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
                      {leafLabel(entry.path)}
                    </Text>
                  </GridItem>
                  <GridItem>
                    {Array.isArray(entry.value) ? (
                      <ArrayValueView value={entry.value} lookupEntity={lookupEntity} />
                    ) : (
                      <Text fontSize="sm" fontWeight="semibold" color="smText" whiteSpace="pre-wrap">
                        {formatLeafValue(entry, { locale, yesLabel: t("Yes"), noLabel: t("No"), resolveEntityName })}
                      </Text>
                    )}
                  </GridItem>
                </Fragment>
              )),
            )}
          </Grid>
        </Box>
      )}

      {present.length === 0 && concerns.length === 0 && (
        <Text fontSize="sm" color="smTextDisabled">
          {t("Confirm this action?")}
        </Text>
      )}

      {missing.length > 0 && (
        <Text fontSize="xs" color="smError" mt="3">
          {t("Missing")}: {missing.map((field) => leafLabel([field.name])).join(", ")} —{" "}
          {t("tell me in the chat and I'll add it.")}
        </Text>
      )}

      <Flex justify="flex-start" gap="2" mt="4">
        <Button
          size="sm"
          bg="smPrimary"
          color="white"
          _hover={{ bg: "smPrimarySolid" }}
          onClick={() => onConfirm(args)}
          isLoading={isBusy}
          isDisabled={missing.length > 0}
        >
          {t("Confirm and execute")}
        </Button>
        <Button size="sm" variant="ghost" onClick={onCancel} isDisabled={isBusy}>
          {t("Cancel")}
        </Button>
      </Flex>
    </Box>
  );
};
